"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { View } from "react-native"
import clsx from "clsx"
import { FormattedMessage } from "react-intl"

import {
  Box,
  FormattedDateFilter,
  FormattedDistanceFilter,
  H1,
  HankLogo,
  IconButton,
  ScrollBox,
  useAsyncStorage,
} from "@bullseye/components"
import { ACTIVITIES_URI, POST_ACTIVITY_URI } from "@bullseye/routes"
import { User } from "@bullseye/types"

import { ActivitySearch, PostActivityButton } from "../../components"
import { PageHeader } from "../../layouts"
import { CategoryFilter } from "./components/CategoryFilter"
import DiscoveryTooltip from "./components/DiscoveryTooltip"
import { LocationFilter } from "./components"
import { ActivitiesList } from "./components/ActivitiesList"
import { useCategoryFilterDiscovery } from "./hooks/useCategoryFilterDiscovery"

type Props = {
  user?: User
  accessToken?: string
}

export const ActivitiesListPage = ({ user, accessToken }: Props) => {
  const [dateFilter, setDateFilter] = useState<{
    [k: string]: string | undefined
  }>()
  const dateFilterRef = useRef<{ reset(): void }>()
  const [distanceFilter, setDistanceFilter] = useState<Record<string, string>>()
  const distanceFilterRef = useRef<{ reset(): void }>()
  const [locationFilter, setLocationFilter] = useState<Record<string, string>>()
  const [categoryFilter, setCategoryFilter] = useState<string[]>([])
  const {
    data: storedCategories,
    isLoading: isCategoryStorageLoading,
    setValue: persistCategories,
  } = useAsyncStorage("activities-category-filter")

  useEffect(() => {
    if (isCategoryStorageLoading) return
    if (Array.isArray(storedCategories) && storedCategories.length > 0) {
      setCategoryFilter(storedCategories as string[])
    }
  }, [storedCategories, isCategoryStorageLoading])

  const handleCategoryChange = useCallback(
    (ids: string[]) => {
      setCategoryFilter(ids)
      void persistCategories(ids.length > 0 ? ids : "")
    },
    [persistCategories],
  )

  const { isNew: isCategoryFilterNew, markSeen: markCategoryFilterSeen } =
    useCategoryFilterDiscovery()
  const mobileAnchorRef = useRef<View>(null)
  const [mobileTooltipDismissed, setMobileTooltipDismissed] = useState(false)

  const handleMobileDiscoverySeen = useCallback(() => {
    markCategoryFilterSeen()
    setMobileTooltipDismissed(true)
  }, [markCategoryFilterSeen])

  const showMobileTooltip = !!isCategoryFilterNew && !mobileTooltipDismissed

  const handleMobileTooltipCta = useCallback(() => {
    handleMobileDiscoverySeen()
  }, [handleMobileDiscoverySeen])

  const resetFilters = () => {
    dateFilterRef?.current?.reset()
    distanceFilterRef?.current?.reset()
    handleCategoryChange([])
  }

  return (
    <>
      <PageHeader
        className={clsx("bg-white py-2 md:hidden", {
          hidden: !user,
        })}
      >
        <HankLogo href={ACTIVITIES_URI} />
        <Box className="flex-row g-2">
          <ActivitySearch />
          <IconButton
            iconType="plus"
            size="lg"
            as="a"
            href={POST_ACTIVITY_URI}
          />
        </Box>
      </PageHeader>
      <Box className="z-[1000] flex w-full items-center bg-white py-4 md:px-4 md:py-6 md:g-6">
        <Box className="hidden w-full max-w-[1200px] flex-row items-center justify-between bg-white md:flex">
          <H1 className="text-2.5xl md:text-4xl">
            <FormattedMessage id="navigation.explore" />
          </H1>
          {user && <PostActivityButton />}
        </Box>
        <ScrollBox
          className="no-scrollbar w-full max-w-[1200px] !overflow-x-scroll md:!overflow-visible"
          contentContainerStyle={{
            gap: 8,
            paddingHorizontal: 16,
          }}
          showsHorizontalScrollIndicator={false}
          horizontal
        >
          <LocationFilter
            user={user}
            onChange={(val) => {
              setLocationFilter(val)
            }}
          />
          <Box className="flex md:hidden">
            <CategoryFilter
              value={categoryFilter}
              onChange={handleCategoryChange}
              isNew={isCategoryFilterNew}
              onDiscoverySeen={handleMobileDiscoverySeen}
              anchorRef={mobileAnchorRef}
              accessToken={accessToken}
            />
          </Box>
          <FormattedDateFilter
            onChange={(_, val) => {
              setDateFilter(val)
            }}
            storageKey="activities-date-filter"
            ref={dateFilterRef}
          />
          <FormattedDistanceFilter
            onChange={(_, val) => {
              setDistanceFilter(val)
            }}
            storageKey="activities-distance-filter"
            ref={distanceFilterRef}
          />
          <Box className="hidden md:flex">
            <CategoryFilter
              value={categoryFilter}
              onChange={handleCategoryChange}
              isNew={isCategoryFilterNew}
              onDiscoverySeen={markCategoryFilterSeen}
              accessToken={accessToken}
            />
          </Box>
        </ScrollBox>
      </Box>
      <DiscoveryTooltip
        visible={showMobileTooltip}
        anchorRef={mobileAnchorRef}
        onCtaPress={handleMobileTooltipCta}
      />
      <ActivitiesList
        resetFilters={resetFilters}
        filters={{
          limit: "25",
          order_by: user ? "start_date" : "distance",
          canceled_at: "null",
          ...dateFilter,
          ...distanceFilter,
          ...locationFilter,
          ...(categoryFilter.length > 0
            ? { categories: categoryFilter.join(",") }
            : {}),
        }}
      />
    </>
  )
}
